import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ObjectActionIconButton } from "@/object-actions/renderers/ObjectActionIconButton";
import type { ResolvedObjectAction } from "@/object-actions/types";
import { cn } from "@/utils";
import { X } from "lucide-react";

interface WorkspaceSelectionBarProps {
  count: number;
  registryActions: ResolvedObjectAction[];
  onClose: () => void;
}

/**
 * Selection indicator + action bar for workspace detail pages.
 *
 * Visually matches the filter-pill vocabulary (`rounded-full`, 36px tall)
 * but is wider and promoted via filled background. Renders inline next
 * to the filter pills; the parent toolbar handles responsive collapse.
 */
export function WorkspaceSelectionBar({
  count,
  registryActions,
  onClose,
}: WorkspaceSelectionBarProps) {
  /** Only surface the bar for multi-selections; a single selection is
   *  already actionable via its per-card/row context menu, and showing
   *  the bar there adds visual noise without adding capability. */
  if (count < 2) return null;

  const labelCtx = {
    kind: {
      type: "multi-selection" as const,
      kinds: [] as Array<"canvas" | "chat" | "file" | "folder">,
    },
    count,
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div
        role="toolbar"
        aria-label={`${count} selected`}
        className={cn(
          "flex items-center gap-0.5 h-9 rounded-full",
          // Match the `blue-500` accent used on selected cards/rows so the
          // toolbar reads as an extension of the selection state.
          "bg-rust text-white shadow-sm ring-1 ring-inset ring-rust/30",
          "pl-1 pr-2 animate-in fade-in-0 slide-in-from-left-1 duration-150",
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Clear selection"
              onClick={onClose}
              className={cn(
                "grid place-items-center h-7 w-7 rounded-full transition-colors cursor-pointer",
                "text-white/85 hover:text-white hover:bg-n-00/15",
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Clear selection</TooltipContent>
        </Tooltip>

        <span className="px-2 text-sm font-medium tabular-nums select-none">
          {count} selected
        </span>

        <span aria-hidden className="h-4 w-px bg-n-00/25 mx-1 shrink-0" />

        {registryActions.map((action) => (
          <ObjectActionIconButton
            key={action.id}
            action={action}
            labelCtx={labelCtx}
            variant="selection-bar"
          />
        ))}
      </div>
    </TooltipProvider>
  );
}
